import { useState, useMemo } from 'react';
import { ExternalLink, Copy, Eye, Check, Search, Globe, Music, Map as MapIcon, Palette } from 'lucide-react';
import { useStore } from '../store/useStore';
import { supabase } from '../lib/supabase';

export function EmbedMapConfig() {
  const currentOrgId = useStore(s => s.currentOrgId);
  const organizations = useStore(s => s.organizations);
  const points = useStore(s => s.points);

  const currentOrg = organizations.find(o => o.id === currentOrgId);
  const embedConfig = currentOrg?.settings?.embedConfig || {};
  const [enabled, setEnabled] = useState(!!embedConfig.enabled);
  const [visiblePoints, setVisiblePoints] = useState<string[]>(embedConfig.visiblePoints || []);
  const [pointDescriptions, setPointDescriptions] = useState<Record<string, string>>(embedConfig.pointDescriptions || {});
  const [showLineUp, setShowLineUp] = useState(embedConfig.showLineUp !== false);
  const [lineUpTitle, setLineUpTitle] = useState(embedConfig.lineUpTitle || 'Line Up');
  const [primaryColor, setPrimaryColor] = useState(embedConfig.primaryColor || '#6366f1');
  const [customLogoUrl, setCustomLogoUrl] = useState(embedConfig.logoUrl || '');
  const [customTitle, setCustomTitle] = useState(embedConfig.title || '');
  const [customCss, setCustomCss] = useState(embedConfig.customCss || '');
  const [headerBg, setHeaderBg] = useState(embedConfig.headerBg || '#111319');
  const [pointColor, setPointColor] = useState(embedConfig.pointColor || '#6366f1');
  const [stageColor, setStageColor] = useState(embedConfig.stageColor || '#f59e0b');
  const [lineupBg, setLineupBg] = useState(embedConfig.lineupBg || '#0A0B0E');
  const [lineupCardBg, setLineupCardBg] = useState(embedConfig.lineupCardBg || '#14161B');
  const [lineupTextColor, setLineupTextColor] = useState(embedConfig.lineupTextColor || '#cbd5e1');
  const [secondaryTitleColor, setSecondaryTitleColor] = useState(embedConfig.secondaryTitleColor || '#94a3b8');
  const [lineupTitleColor, setLineupTitleColor] = useState(embedConfig.lineupTitleColor || embedConfig.primaryColor || '#6366f1');
  const [searchTerm, setSearchTerm] = useState('');
  const isSuperAdmin = useStore(s => s.currentUser?.role === 'superadmin');
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  const embedUrl = `${window.location.origin}/?embed=${currentOrgId}`;
  const iframeCode = `<iframe src="${embedUrl}" width="100%" height="600" frameborder="0" style="border-radius:12px"></iframe>`;

  const filteredPoints = useMemo(() => {
    if (!searchTerm.trim()) return points;
    return points.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [points, searchTerm]);

  const visibleSet = new Set(visiblePoints);
  const allSelected = filteredPoints.length > 0 && filteredPoints.every(p => visibleSet.has(p.id));

  const togglePoint = (pointId: string) => {
    setVisiblePoints(prev =>
      prev.includes(pointId) ? prev.filter(id => id !== pointId) : [...prev, pointId]
    );
  };

  const toggleAll = () => {
    if (allSelected) {
      // Deselect all visible
      setVisiblePoints(prev => prev.filter(id => !filteredPoints.some(p => p.id === id)));
    } else {
      // Select all visible
      const newIds = filteredPoints.map(p => p.id).filter(id => !visibleSet.has(id));
      setVisiblePoints(prev => [...prev, ...newIds]);
    }
  };

  const handleSave = async () => {
    if (!currentOrgId) return;
    setSaving(true);
    setSaveError('');

    // Read fresh settings from DB to avoid stale overwrites
    const { data: freshOrg } = await supabase.from('organizations').select('settings').eq('id', currentOrgId).single();
    const dbSettings = (freshOrg?.settings || currentOrg?.settings || { minRestTimeHours: 8 }) as Record<string, any>;

    const newSettings = {
      ...dbSettings,
      embedConfig: {
        enabled,
        visiblePoints,
        pointDescriptions,
        showLineUp,
        lineUpTitle: lineUpTitle || 'Line Up',
        primaryColor: primaryColor || '#6366f1',
        logoUrl: customLogoUrl || '',
        title: customTitle || '',
        customCss: customCss || '',
        headerBg: headerBg || '#111319',
        pointColor: pointColor || '#6366f1',
        stageColor: stageColor || '#f59e0b',
        lineupBg: lineupBg || '#0A0B0E',
        lineupCardBg: lineupCardBg || '#14161B',
        lineupTextColor: lineupTextColor || '#cbd5e1',
        secondaryTitleColor: secondaryTitleColor || '#94a3b8',
        lineupTitleColor: lineupTitleColor || primaryColor || '#6366f1',
      },
    };

    // Update Supabase
    const { error: updateError } = await supabase.from('organizations').update({ settings: newSettings }).eq('id', currentOrgId);

    if (updateError) {
      setSaveError(updateError.message);
      setSaving(false);
      return;
    }

    // Verify by reading back
    const { data: verifyOrg } = await supabase.from('organizations').select('settings').eq('id', currentOrgId).single();
    console.log('[EmbedMap] Saved embedConfig:', (verifyOrg?.settings as any)?.embedConfig);

    // Update local Zustand state
    useStore.setState((state: any) => ({
      organizations: state.organizations.map((o: any) =>
        o.id === currentOrgId ? { ...o, settings: newSettings } : o
      ),
    }));

    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!currentOrgId || !currentOrg) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Globe className="w-8 h-8 mx-auto mb-3 opacity-40" />
        <p className="text-xs">Aucune organisation sélectionnée.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Enable Toggle ──────────────────────────────────────── */}
      <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-5 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Globe className="w-4 h-4 text-indigo-400" /> Activer la carte publique
          </h3>
          <p className="text-[10px] text-slate-400 mt-1">Rend une carte interactive accessible publiquement via un lien.</p>
        </div>
        <button
          onClick={() => setEnabled(!enabled)}
          className={`relative w-12 h-6 rounded-full transition-all ${enabled ? 'bg-indigo-600' : 'bg-[#2D3139]'}`}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all shadow-md ${enabled ? 'left-6' : 'left-0.5'}`} />
        </button>
      </div>

      {enabled && (
        <>
          {/* ── Point Selection ────────────────────────────────── */}
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-5 space-y-4">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <MapIcon className="w-4 h-4 text-indigo-400" /> Points visibles sur la carte
            </h3>
            <p className="text-[10px] text-slate-400">Sélectionnez les points à afficher sur la carte publique. Les points avec localisation apparaissent sur la carte, les autres dans une liste.</p>
            {visiblePoints.length > 0 && (
              <p className="text-[9px] text-amber-400">
                {visiblePoints.filter(id => !points.find(p => p.id === id)?.location).length} point(s) sélectionné(s) sans localisation GPS — ils apparaîtront dans la liste, pas sur la carte.
              </p>
            )}

            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Rechercher un point..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg pl-9 pr-4 py-2 text-xs focus:ring-1 focus:ring-indigo-500 outline-none text-slate-200"
              />
            </div>

            <button
              onClick={toggleAll}
              className="text-[10px] text-indigo-400 hover:text-indigo-300 font-bold uppercase tracking-wider"
            >
              {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>

            <div className="space-y-1 max-h-64 overflow-y-auto custom-scrollbar">
              {filteredPoints.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-4">Aucun point trouvé.</p>
              ) : (
                filteredPoints.map(p => {
                  const isSelected = visibleSet.has(p.id);
                  const hasLocation = !!p.location;
                  return (
                    <label
                      key={p.id}
                      className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all ${
                        isSelected ? 'bg-indigo-600/10 border border-indigo-500/30' : 'hover:bg-[#1A1D23] border border-transparent'
                      } ${!hasLocation ? 'opacity-50' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!hasLocation}
                        onChange={() => togglePoint(p.id)}
                        className="w-3.5 h-3.5 text-indigo-600 border-slate-600 bg-[#0A0B0E] rounded focus:ring-indigo-500 flex-shrink-0"
                      />
                      <span className="flex-1 text-xs text-slate-300 truncate">{p.name}</span>
                      {p.isStage && <span className="text-[8px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded font-bold flex-shrink-0">STAGE</span>}
                      {!hasLocation && <span className="text-[8px] text-slate-600 flex-shrink-0">pas de localisation</span>}
                    </label>
                  );
                })
              )}
            </div>

            <p className="text-[9px] text-slate-500">{visiblePoints.length} point(s) sélectionné(s)</p>
          </div>

          {/* ── Custom Descriptions ─────────────────────────────── */}
          {visiblePoints.length > 0 && (
            <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-5 space-y-4">
              <h3 className="text-sm font-bold text-white">Descriptions personnalisées</h3>
              <p className="text-[10px] text-slate-400">Ajoutez une description qui apparaîtra quand un visiteur cliquera sur le point. Laissez vide pour utiliser la description par défaut.</p>
              <div className="space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
                {visiblePoints.map(pointId => {
                  const pt = points.find(p => p.id === pointId);
                  if (!pt) return null;
                  return (
                    <div key={pointId} className="space-y-1.5">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        {pt.name} {pt.isStage && '🎤'}
                      </label>
                      <textarea
                        value={pointDescriptions[pointId] || ''}
                        onChange={(e) => setPointDescriptions(prev => ({ ...prev, [pointId]: e.target.value }))}
                        placeholder={pt.descriptionMarkdown || 'Description par défaut...'}
                        rows={2}
                        className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg p-2.5 text-xs text-slate-200 focus:ring-1 focus:ring-indigo-500 outline-none resize-none placeholder:text-slate-600"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Line Up Settings ────────────────────────────────── */}
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-5 space-y-4">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Music className="w-4 h-4 text-amber-400" /> Line Up
            </h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-300 font-bold">Afficher l'onglet Line Up</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Montre la timeline des artistes programmés</p>
              </div>
              <button
                onClick={() => setShowLineUp(!showLineUp)}
                className={`relative w-12 h-6 rounded-full transition-all ${showLineUp ? 'bg-amber-600' : 'bg-[#2D3139]'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all shadow-md ${showLineUp ? 'left-6' : 'left-0.5'}`} />
              </button>
            </div>
            {showLineUp && (
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Titre de l'onglet</label>
                <input
                  type="text"
                  value={lineUpTitle}
                  onChange={(e) => setLineUpTitle(e.target.value)}
                  placeholder="Line Up"
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-2 text-xs text-white focus:ring-1 focus:ring-indigo-500 outline-none mt-1"
                />
              </div>
            )}
          </div>

          {/* ── Personalisation ─────────────────────────────────── */}
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-5 space-y-4">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Palette className="w-4 h-4 text-pink-400" /> Personnalisation
            </h3>
            <p className="text-[10px] text-slate-400">Adaptez l'apparence de l'embed pour l'harmoniser avec votre marque.</p>

            {/* Row 1: Primary + Logo + Title */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <ColorPicker label="Couleur principale" value={primaryColor} onChange={setPrimaryColor} desc="Tabs, accents, badges" />
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">URL du logo</label>
                <input type="text" value={customLogoUrl} onChange={(e) => setCustomLogoUrl(e.target.value)} placeholder="https://... (vide = logo org)" className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-2 text-xs text-white focus:ring-1 focus:ring-indigo-500 outline-none" />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Titre de l'embed</label>
                <input type="text" value={customTitle} onChange={(e) => setCustomTitle(e.target.value)} placeholder={currentOrg?.name || "Nom de l'organisation"} className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-2 text-xs text-white focus:ring-1 focus:ring-indigo-500 outline-none" />
              </div>
            </div>

            {/* Row 2: Interface colors */}
            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider pt-2">Couleurs de l'interface</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <ColorPicker label="Fond du menu" value={headerBg} onChange={setHeaderBg} desc="Header + barre d'onglets" />
              <ColorPicker label="Icônes points" value={pointColor} onChange={setPointColor} desc="Marqueurs normaux sur la carte" />
              <ColorPicker label="Icônes stages" value={stageColor} onChange={setStageColor} desc="Marqueurs de scène sur la carte" />
            </div>

            {/* Row 3: Line Up colors */}
            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider pt-2">Couleurs du Line Up</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <ColorPicker label="Titre principal" value={lineupTitleColor} onChange={setLineupTitleColor} desc="Titre Line Up • Org" />
              <ColorPicker label="Titres secondaires" value={secondaryTitleColor} onChange={setSecondaryTitleColor} desc="Dates + noms de scènes" />
              <ColorPicker label="Fond timeline" value={lineupBg} onChange={setLineupBg} desc="Arrière-plan de la liste" />
              <ColorPicker label="Fond cartes" value={lineupCardBg} onChange={setLineupCardBg} desc="Fond des cartes artistes" />
              <ColorPicker label="Texte" value={lineupTextColor} onChange={setLineupTextColor} desc="Nom des artistes" />
            </div>
          </div>

          {/* ── CSS Editor (superadmin only) ────────────────────── */}
          {isSuperAdmin && (
            <div className="bg-[#14161B] border border-amber-500/30 rounded-2xl p-5 space-y-4">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Palette className="w-4 h-4 text-amber-400" /> CSS Personnalisé
              </h3>
              <p className="text-[10px] text-slate-400">
                Injectez du CSS brut dans la page embed. Utilisez les sélecteurs suivants :
                <code className="text-amber-400 bg-[#0A0B0E] px-1.5 py-0.5 rounded ml-1 text-[9px]">
                  .embed-header, .embed-tab, .embed-lineup-artist, .embed-lineup-stage, .embed-lineup-time
                </code>
              </p>
              <textarea
                value={customCss}
                onChange={(e) => setCustomCss(e.target.value)}
                placeholder={`.embed-header {\n  background: #1a1a2e;\n}\n.embed-lineup-time {\n  font-weight: bold;\n  font-size: 12px;\n}`}
                rows={10}
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg p-3 text-xs text-green-400 font-mono focus:ring-1 focus:ring-amber-500 outline-none resize-y placeholder:text-slate-700"
                spellCheck={false}
              />
            </div>
          )}

          {/* ── Preview ─────────────────────────────────────────── */}
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-5 space-y-4">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Eye className="w-4 h-4 text-indigo-400" /> Aperçu
            </h3>
            <div className="flex gap-3">
              <a
                href={embedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Ouvrir la Map
              </a>
              {showLineUp && (
                <a
                  href={embedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-2.5 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 font-bold text-xs rounded-xl border border-amber-500/30 transition-all flex items-center justify-center gap-2"
                >
                  <Music className="w-3.5 h-3.5" /> Ouvrir Line Up
                </a>
              )}
            </div>
          </div>

          {/* ── Embed Link ──────────────────────────────────────── */}
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-5 space-y-4">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-indigo-400" /> Lien Embed
            </h3>

            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">URL directe</label>
              <div className="flex mt-1">
                <input
                  type="text"
                  readOnly
                  value={embedUrl}
                  className="flex-1 bg-[#0A0B0E] border border-[#2D3139] rounded-l-lg px-3 py-2 text-xs text-slate-300 font-mono outline-none"
                />
                <button
                  onClick={() => copyToClipboard(embedUrl)}
                  className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-r-lg text-white transition-all flex items-center gap-1"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  <span className="text-[10px] font-bold uppercase">{copied ? 'Copié' : 'Copier'}</span>
                </button>
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Code iframe</label>
              <div className="flex mt-1">
                <input
                  type="text"
                  readOnly
                  value={iframeCode}
                  className="flex-1 bg-[#0A0B0E] border border-[#2D3139] rounded-l-lg px-3 py-2 text-xs text-slate-300 font-mono outline-none"
                />
                <button
                  onClick={() => copyToClipboard(iframeCode)}
                  className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-r-lg text-white transition-all flex items-center gap-1"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  <span className="text-[10px] font-bold uppercase">{copied ? 'Copié' : 'Copier'}</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Save Button ────────────────────────────────────────── */}
      {saveError && (
        <div className="bg-rose-500/10 border border-rose-500/30 p-3 rounded-xl text-xs text-rose-400 font-bold">
          Erreur de sauvegarde : {saveError}
        </div>
      )}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all ${
            saved
              ? 'bg-emerald-600 text-white'
              : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-md shadow-indigo-600/20'
          } disabled:opacity-50`}
        >
          {saved ? '✓ Sauvegardé' : saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
      </div>
    </div>
  );
}

// ─── Color Picker Helper ────────────────────────────────────────────
function ColorPicker({ label, value, onChange, desc }: {
  label: string; value: string; onChange: (v: string) => void; desc?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
          className="w-9 h-9 rounded-lg border-2 border-[#2D3139] bg-transparent cursor-pointer flex-shrink-0" />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-2.5 py-1.5 text-[11px] text-white font-mono focus:ring-1 focus:ring-indigo-500 outline-none" />
      </div>
      {desc && <p className="text-[8px] text-slate-600">{desc}</p>}
    </div>
  );
}
